import { useAuthStore } from '../store/useAuthStore';
import { authService } from '../api/authService';
import { LoginRequest } from '@/core/sdk/modules/administracion/authClient';
import { MENSAJES_ERROR } from '../constants';
import { useSesionBloqueada } from './useSesionBloqueada';
import { getDeviceId } from '@/core/utils/deviceId';

/**
 * NOTA P1 Lite: Cross-BC Dependency Eliminada
 * 
 * - Anteriormente: auth importaba useMenuStore (violación DDD)
 * - Ahora: useMenu se auto-ejecuta en DashboardLayout (correcto)
 * - Login ya NO llama a loadMenu() manualmente
 */

export const useAuth = () => {
    const { isAuthenticated, user, token, login, logout } = useAuthStore();
    const { desbloquearSesion } = useSesionBloqueada();

    const handleLogin = async (credentials: LoginRequest) => {
        // NUEVO: Generar/obtener deviceId único para Feature 4 (Multi-sesión)
        // Backend usará esto para cerrar automáticamente otras sesiones del usuario
        const dispositivoId = getDeviceId();

        const response = await authService.login({
            ...credentials,
            DispositivoId: dispositivoId,
        });

        if (!response.success) {
            throw new Error(response.message || MENSAJES_ERROR.ERROR_LOGIN);
        }

        // Mapear la respuesta del backend al formato del store
        const userData = {
            id: response.data.usuario.id,
            userId: response.data.usuario.usuarioId, // Identificador de negocio (string)
            email: response.data.usuario.correo,
            name: response.data.usuario.nombre,
            roles: response.data.usuario.roles,
            cuentaActiva: response.data.usuario.cuentaActiva,
        };

        login(
            userData,
            response.data.token,
            response.data.tokenExpiracion,
            response.data.requiereCambioContrasena
        );

        // ✅ P1 Lite: Menu se carga automáticamente en DashboardLayout vía useMenu hook
        // (No necesitamos llamar loadMenu() aquí - cross-BC eliminado)

        return response;
    };

    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            // Si falla el logout en el servidor, igualmente limpiar el estado local
            console.error('Error al cerrar sesión en el servidor:', error);
        } finally {
            logout();
            // CRÍTICO: Limpiar estado de sesión bloqueada para evitar bloqueo permanente
            desbloquearSesion();
            // ✅ P1 Lite: Ya NO limpiamos menu aquí (cross-BC eliminado)
            // Menu se limpia automáticamente cuando user sale del dashboard
        }
    };

    return {
        isAuthenticated,
        user,
        token,
        login: handleLogin,
        logout: handleLogout,
    };
};